import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiEye, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { Usuario } from 'pages/@types';
import config from 'config/environment';
import api from 'services/api';
import AppError from 'utils/AppError';
import { useModal } from 'hooks/modal';
import { TipoSolicitacaoChamada } from 'utils/variables';
import NoSolicitacaoChamada from 'assets/Fill out-pana.svg';

import Flag from 'components/Flag';
import Loader from 'components/ContentLoader/GenericLoader';
import NotFound from 'components/NotFound';
import Card from 'components/Card';
import Pagination from 'components/Pagination';
import Button from 'components/Button';
import Avatar from 'components/Avatar';

import { Container, NovoInfo } from './styles';
import { Table } from 'styles/global';

interface SolicitacaoChamada {
  id: number;
  nome: string;
  tipo: 'jornalismo' | 'especiais' | 'pgmlinha';
  solicitante: Usuario;
  dataexibicao: string;
}

export default function SolicitacaoChamada() {
  // react-router-dom
  const navigate = useNavigate();

  /**
   * USE STATE
   */
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoChamada[]>([]);

  /**
   * Hooks
   */
  const { showModal, hideModal } = useModal();

  const handleRedirect = useCallback(
    option => {
      hideModal();
      navigate(`/solicitacaochamada/novo/${option}`);
    },
    [hideModal, navigate],
  );

  const renderNovo = useCallback(() => {
    return (
      <NovoInfo>
        <p>Oi!</p>
        <p>
          Você está prestes a abrir a solicitação de uma chamada para promover
          um programa da nossa grade. :D
        </p>
        <p>
          Certifique-se de que está respondendo da maneira mais completa
          possível, para que sejamos coerentes na comunicação.
        </p>
        <p>
          Para <b>lançamentos</b> (sejam séries ou projetos especiais), pedimos
          que entre em contato com a coordenação da Promo já na etapa de
          planejamento do material, para trabalharmos e criarmos juntos uma
          campanha efetiva e com o melhor tom de voz.
        </p>
        <p>Combinado?</p>
        <p>
          Em caso de dúvidas, por favor envie um e-mail para{' '}
          <span>patriciahoca@rpc.com.br</span>, ou entre em contato pelo ramal{' '}
          <span>7952</span>
        </p>
        <p>Agora, vamos lá!</p>
        <div>
          <Button
            color="success"
            onClick={() => handleRedirect(TipoSolicitacaoChamada.JORNALISMO)}
          >
            JORNALISMO / ESPORTE
          </Button>
          <Button
            color="primary"
            onClick={() => handleRedirect(TipoSolicitacaoChamada.ESPECIAIS)}
          >
            ESPECIAIS DE ENTRETENIMENTO
          </Button>
          <Button
            color="secondary"
            onClick={() => handleRedirect(TipoSolicitacaoChamada.PGMLINHA)}
          >
            PROGRAMAS DE LINHA
          </Button>
        </div>
      </NovoInfo>
    );
  }, [handleRedirect]);

  const handleNovo = useCallback(() => {
    showModal({
      width: '65vw',
      title: 'SOLICITAÇÃO DE CHAMADAS - PROMO RPC',
      component: renderNovo(),
    });
  }, [renderNovo, showModal]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadSolicitacao() {
      setLoading(true);
      try {
        const response = await api.get<{
          solicitacoes: SolicitacaoChamada[];
          pages: number;
        }>('/solicitacaochamada', {
          signal: controller.signal,
          params: { page },
        });
        if (response && response.data) {
          setSolicitacoes(response.data.solicitacoes);
          setPages(response.data.pages);
        }
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    loadSolicitacao();
    return () => {
      controller.abort();
    };
  }, [page]);

  return (
    <Container>
      <Card>
        <header>
          <h4>LISTA DE SOLICITAÇÕES</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <Button
            color="primary"
            onClick={handleNovo}
            tooltip="Criar nova Solicitação"
          >
            NOVA SOLICITAÇÃO
            <FiPlus />
          </Button>
        </header>
        {loading ? (
          <Loader type="solicitacao_chamada" />
        ) : solicitacoes && solicitacoes.length ? (
          <div style={{ padding: `0 15px` }}>
            <Table>
              <thead>
                <tr>
                  <th>NOME</th>
                  <th style={{ width: 155, textAlign: 'center' }}>TIPO</th>
                  <th style={{ width: 200, textAlign: 'center' }}>
                    SOLICITANTE
                  </th>
                  <th style={{ width: 150, textAlign: 'center' }}>
                    VISUALIZAR
                  </th>
                  {/* <th style={{ width: 200 }}>DATA DE EXIBIÇÃO</th> */}
                </tr>
              </thead>
              <tbody>
                {solicitacoes.map(s => (
                  <tr key={s.id}>
                    <td>{s.nome}</td>
                    <td>
                      <Flag
                        type={s.tipo}
                        name={s.tipo
                          .replace(
                            TipoSolicitacaoChamada.JORNALISMO,
                            'JORN / ESPORTE',
                          )
                          .replace(
                            TipoSolicitacaoChamada.PGMLINHA,
                            'PGM DE LINHA',
                          )}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Avatar
                        size="40"
                        name={s.solicitante?.nome}
                        src={s.solicitante?.avatar_url}
                        round
                      />
                    </td>
                    <td style={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        id={`id_visualizar_${s.id}`}
                        fill
                        color="font"
                        round
                        onClick={e => {
                          e.preventDefault();
                          window.open(
                            `${config.API_URL}/solicitacaochamadafile/${s.id}`,
                            '_blank',
                          );
                        }}
                      >
                        <FiEye />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <NotFound img={NoSolicitacaoChamada} title="Solicitação Chamada">
            Nenhuma Solicitação de Chamada encontrada
          </NotFound>
        )}
      </Card>
    </Container>
  );
}
